import {Component, Input, OnInit} from '@angular/core';
import {NgbActiveModal} from '@ng-bootstrap/ng-bootstrap';
import {TranslateService} from '@ngx-translate/core';
import {NotificationService} from '@services/notification/notification.service';
import {Organization} from '@models/organization/organization';
import {ConfirmationDialogService} from '@services/confirmation-dialog.service';
import {LoadingIndicatorService} from '@services/loading-indicator/loading-indicator.service';
import {UserService} from '@services/administration/user/user.service';
import {catchError, switchMap} from 'rxjs/operators';
import {throwError} from 'rxjs';
import {ModalDialogDismissOption} from '@shared/confirmation-dialog/modal-dialog-dismiss-option';

@Component({
    selector: 'scitis-users-ban-modal',
    templateUrl: './users-ban-modal.component.html',
    styleUrls: ['./users-ban-modal.component.scss'],
    standalone: false
})
export class UsersBanModalComponent implements OnInit {

  @Input()
  public organizationsList: Organization[] = [];


  public filteredOrganizations: Organization[] = [];
  public searchedOrganizationTerm: string = '';
  private CURRENT_PAGE_NAME = 'UsersBanModalComponent';
  private banTriggered: boolean = false;

  constructor(private activeModal: NgbActiveModal,
              private loadingIndicator: LoadingIndicatorService,
              private translateService: TranslateService,
              private confirmationDialogService: ConfirmationDialogService,
              private notificationService: NotificationService,
              private userService: UserService) {
  }

  ngOnInit(): void {
    this.filteredOrganizations = [...this.organizationsList];
  }

  onCloseModal(): void {
    if (this.banTriggered) {
      this.activeModal.close();
    } else {
      this.activeModal.dismiss(ModalDialogDismissOption.CLOSE);
    }
  }

  public onBan(organization: Organization | null) {
    if (!organization) {
      return;
    }
    this.banUsersOfOrganization(organization);

  }

  public onSearchForOrganization($event: Event) {
    this.filteredOrganizations = this.getFilteredOrganizations($event);
  }


  private banUsersOfOrganization(organization: Organization): void {
    const boldOrganizationName = organization.name.toBold();
    console.log(`${this.CURRENT_PAGE_NAME}. ban all users of organization: ${boldOrganizationName}`);
    const banMessageTitle = this.translateService.instant('Ban all users of organization');
    const banMessageBody = this.translateService.instant('Do you really want to ban all users of the organization %s ?').replace('%s', boldOrganizationName);
    const banMessageOK = this.translateService.instant('Ok');

    this.confirmationDialogService.handleConfirmationDialog(banMessageTitle, banMessageBody, '', banMessageOK)
    .pipe(
        switchMap((userConfirmed: boolean) => {
          if (userConfirmed) {
            this.banTriggered = true;
            this.loadingIndicator.show();
            return this.userService.banAllUsersForOrganization(organization.id).pipe(catchError(error => throwError({confirmError: error})));
          } else {
            this.banTriggered = false;
            return throwError({confirmError: userConfirmed});
          }
        })
    ).subscribe({
      next: () => {
        this.loadingIndicator.hide();
        const message = this.translateService.instant('All users of organization #organization were banned').replace('#organization', boldOrganizationName);
        this.notificationService.success(message, this.CURRENT_PAGE_NAME, false);
      }, error: error => {
        if (error?.confirmError) {
          this.loadingIndicator.hide();
          const message = this.translateService.instant('Banning users of organization #organization failed').replace('#organization', boldOrganizationName);
          this.notificationService.error(message, error, this.CURRENT_PAGE_NAME, true);
        } else {
          this.loadingIndicator.hide();
        }
      }
    });
  }

  private getFilteredOrganizations(event: Event): Organization[] {
    this.searchedOrganizationTerm = this.getSearchTermValue(event);
    const organizations = [...this.organizationsList];
    const filteredOrganizations: Organization[] = [];
    organizations.forEach(organization => {
      if (this.searchTermFoundInOrganizationNameOrDescription(organization, this.searchedOrganizationTerm)) {
        filteredOrganizations.push(organization);
      }
    });
    return filteredOrganizations;
  }

  private getSearchTermValue(event: Event): string {
    if (!event || !event.target) {
      return '';
    } else {
      const element = event.target as HTMLInputElement;
      if (!element || !element.value) {
        return '';
      } else {
        return element.value;
      }
    }
  }

  private searchTermFoundInOrganizationNameOrDescription(organization: Organization, term: string): boolean {
    const lowercaseTerm = term.toLowerCase();
    return organization.description.toLowerCase().includes(lowercaseTerm) || organization.name.toLowerCase().includes(lowercaseTerm);
  }
}
